/**
 * « Mon historique de lives » (`tutor-lives-history`) : ce que `<TutorLivesHistoryPage>` montre, lu
 * dans le store (patron A, une liste paginée).
 *
 * Les séances arrivent de `tutors.liveOccurrences` (le réducteur remplace à la page 1, ajoute après) ;
 * la tranche `getTutorLiveOccurrences` dit le chargement : le squelette tant qu'aucune ligne n'est là,
 * la flèche pour une relecture de la page 1, le pied de liste pour la suite. La page en cours et le
 * total vivent dans `useTutorLivesHistoryData` (copiés de `meta`).
 */
import moment from "moment"
import { createSelector } from "reselect"

import {
    toTutorHistoryOccurrences,
    type ApiTutorHistoryOccurrence,
    type TutorHistoryOccurrenceRow,
} from "../../../adapters/tutor/Lives/tutorHistoryAdapter"
import { apiErrorMessage } from "../../../ui/hooks/apiErrorMessage"

export type TutorLivesHistoryViewModel = {
    occurrences: TutorHistoryOccurrenceRow[]
    loading: boolean
    refreshing: boolean
    loadingMore: boolean
    /** Un échec sans rien à montrer : le bandeau de la page. */
    error: unknown | null
    /** Un échec de la suite, des lignes déjà là : le pied de liste. */
    loadMoreError: string | null
}

type Slice = { request?: boolean; error?: unknown; urlParams?: { page?: number | string } } | undefined

type HistoryState = {
    data?: { tutors?: { liveOccurrences?: ApiTutorHistoryOccurrence[] | null } | null }
    api?: { getTutorLiveOccurrences?: Slice }
}

const root = (state: unknown) => state as HistoryState

/** La phrase d'un échec de la suite, quand le serveur n'en donne pas. */
const LOAD_MORE_FAILED = "Impossible de charger la suite de l'historique"

const selectHistory = createSelector(
    [
        (state: unknown) => root(state)?.data?.tutors?.liveOccurrences ?? null,
        (state: unknown) => !!root(state)?.api?.getTutorLiveOccurrences?.request,
        (state: unknown) => root(state)?.api?.getTutorLiveOccurrences?.error ?? null,
        (state: unknown) => Number(root(state)?.api?.getTutorLiveOccurrences?.urlParams?.page ?? 1),
        // « Non fait » / « À faire » se relisent au changement de jour.
        () => new Date().toDateString(),
    ],
    (raws, requesting, error, page): TutorLivesHistoryViewModel => {
        const occurrences = toTutorHistoryOccurrences(raws, moment())
        const hasRows = occurrences.length > 0
        return {
            occurrences,
            loading: requesting && !hasRows,
            refreshing: requesting && hasRows && page <= 1,
            loadingMore: requesting && hasRows && page > 1,
            error: hasRows ? null : error,
            loadMoreError: hasRows && error != null ? apiErrorMessage(error, LOAD_MORE_FAILED) : null,
        }
    }
)

export function tutorLivesHistoryViewModel(state: unknown): TutorLivesHistoryViewModel {
    return selectHistory(state)
}
